/**
 * External dependencies.
 */
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import SelectSearch from "react-select-search";
import { Trans } from "@lingui/macro";
import { ToastContainer, toast } from "react-toastify";

/**
 * Internal dependencies.
 */
import { fetchAPI } from "utils";
// import * as fbq from "../../lib/fpixel";

// styles

import classNames from "classnames";
import "react-toastify/dist/ReactToastify.css";

// components

import Field from "components/field/field";
import FieldFile from "components/field-file/field-file";

import type { FormFieldProp } from "types/forms";

interface FormContactProp {
  data: {
    FormField: FormFieldProp[];
    SubmitText: string;
  };
}

const FormContact: React.FC<FormContactProp> = ({ data }) => {
  const { FormField, SubmitText } = data ?? {};
  const names = FormField?.reduce((acc: any, val: any) => {
    acc[val.name] = "";
    return acc;
  }, {});

  const {
    reset,
    resetField,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<any>({
    mode: "onChange",
    defaultValues: names,
  });
  const onSubmit: SubmitHandler<any> = (submitData) => {
    const newData = new FormData();
    const dataObj: any = {
      publishedAt: null,
    };
    FormField.map((item: FormFieldProp) => {
      if (item.Type !== "file") {
        dataObj[item.name] = submitData[item.name];
      } else if (submitData[item.name]) {
        newData.append(
          `files.${item.name}`,
          submitData[item.name],
          submitData[item.name].name
        );
      }
    });

    newData.append("data", JSON.stringify(dataObj));
    return fetchAPI(
      "/contact/submit",
      {},
      {
        method: "post",
        headers: {},
        body: newData,
      }
    )
      .then((res) => {
        if (res.error) {
          toast.error("There was an error submitting the form!");
          return;
        }

        // fbq.event("Contact");
        toast("Form submitted!");
        reset();
      })
      .catch(() => {
        toast.error("There was an error submitting the form!");
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="form-contact">
      {FormField?.map((item: FormFieldProp, index: number) => (
        <div key={index}>
          <label>
            {item.Label} {item.Optional && <span>(Optional)</span>}
          </label>
          <Controller
            name={item.name}
            control={control}
            rules={{ required: !item.Optional }}
            render={({ field }) => {
              switch (item.Type) {
                case "select":
                  return item.SelectOptions ? (
                    <SelectSearch
                      options={item.SelectOptions}
                      onChange={field.onChange}
                      value={field.value}
                      className={classNames(
                        "select-search",
                        "select-search-form",
                        {
                          error: errors[item.name],
                        }
                      )}
                      placeholder={item.Placeholder || "-"}
                    />
                  ) : (
                    <></>
                  );
                case "textarea":
                  return (
                    <div className="form-control">
                      <textarea
                        onChange={field.onChange}
                        placeholder={item.Placeholder}
                        value={field.value}
                        className={classNames("field field--message", {
                          error: errors[item.name],
                        })}
                      ></textarea>
                    </div>
                  );
                case "file":
                  return (
                    <FieldFile
                      onChange={field.onChange}
                      placeholder={
                        <Trans>
                          <strong>Add file</strong> or drop files here
                        </Trans>
                      }
                      resetField={resetField}
                      value={field.value}
                      className={classNames({ error: errors[item.name] })}
                    />
                  );
                default:
                  return (
                    <Field
                      onChange={field.onChange}
                      placeholder={item.Placeholder}
                      type={item.Type}
                      value={field.value}
                      className={classNames({ error: errors[item.name] })}
                    />
                  );
              }
            }}
          />
        </div>
      ))}
      <button
        type="submit"
        aria-label="button"
        disabled={isSubmitting}
        className="btn btn--fill form-contact__btn"
      >
        {isSubmitting ? "Submitting" : SubmitText}
      </button>

      <ToastContainer position="bottom-center" autoClose={2000} />
    </form>
  );
};

export default FormContact;
